import styled from '@emotion/styled';
import { Variables } from '../base/variables/variables';

/**
 * The container itself — a flex column, and when it renders a panel of its own, a flex ITEM as well.
 *
 * `TabContent` can only divide height its parent actually has. Left shrink-wrapped, the wrapper has none:
 * the panel collapses to its content and a parent that centres its children — `PageLayoutRow` does — floats
 * the whole block down the page with an empty band above the tab strip. Everything renders and every text
 * check still passes; it is only wrong to look at, which is why this was found by eye and not by a verifier.
 *
 * `fills` is false for a caller that renders the panel elsewhere and passes `panelId`, so that layout is
 * byte-for-byte what it was.
 */
export const TabContainerWrapper = styled.div<{ fills: boolean }>`
    display: flex;
    flex-direction: column;
    width: auto;
    ${(props): string => (props.fills ? 'flex: 1 1 auto; min-height: 0;' : '')}
`;

export type TabVariant = 'folder' | 'underline';

/**
 * Two shapes, because two surfaces genuinely want different things.
 *
 * `folder` is the original: a filled bar of tabs that read as the top edge of the panel below them,
 * which suits a tab strip welded to its content. `underline` is a flat row with a rule under the
 * active tab, for a page where the tabs FILTER a view rather than switch panels — the denials
 * dashboard, where the table below is the same table either way.
 *
 * `folder` stays the default so every existing caller is untouched.
 */
export const TabList = styled.div<{ variant: TabVariant }>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: ${(props): string => (props.variant === 'underline' ? '24px' : '0px')};
    background: ${(props): string => (props.variant === 'underline' ? 'transparent' : Variables.Swatches.Gray3)};
    border-bottom: ${(props): string =>
        props.variant === 'underline' ? `1px solid ${Variables.Swatches.Gray3}` : 'none'};
    border-radius: ${(props): string => (props.variant === 'underline' ? '0' : '4px 4px 0px 0px')};
    height: ${(props): string => (props.variant === 'underline' ? '36px' : '32px')};
    width: 100%;
`;

/**
 * The tab list and its `trailing` controls, on one row. Always rendered, so showing or hiding the controls
 * never remounts the tabs; without controls the list is its only child and fills it. For `underline` the
 * rule runs under the whole row; the list's own rule lands on the same pixel row, so the two read as one line.
 */
export const TabBar = styled.div<{ variant: TabVariant }>`
    display: flex;
    align-items: flex-end;
    gap: 16px;
    box-shadow: ${(props): string =>
        props.variant === 'underline' ? `inset 0 -1px 0 ${Variables.Swatches.Gray3}` : 'none'};
`;

export const TabTrailing = styled.div`
    display: flex;
    flex: none;
    align-items: center;
    align-self: center;
    gap: 16px;
    margin-left: auto;
`;

interface ITabProps {
    active: boolean;
    zIndex: number;
    variant: TabVariant;
}

export const Tab = styled.button<ITabProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: ${props => (props.variant === 'underline' ? '6px 0' : '6px 12px')};
    gap: 6px;
    height: 100%;
    min-width: fit-content;
    background: ${props =>
        props.variant === 'underline' ? 'transparent' : props.active ? Variables.Swatches.Gray1 : 'transparent'};
    border-radius: ${props => (props.variant === 'underline' ? '0' : '4px 4px 0px 0px')};
    border: none;
    box-shadow: ${props =>
        props.variant === 'underline' && props.active ? `inset 0 -2px 0 0 ${Variables.Colors.b300}` : 'none'};
    cursor: pointer;
    outline: none;
    user-select: none;
    z-index: ${props => props.zIndex};
    flex-shrink: 0;
    transition: background 0.15s ease;

    &:hover {
        background: ${props =>
            props.variant === 'underline'
                ? 'transparent'
                : props.active
                ? Variables.Swatches.Gray1
                : Variables.Swatches.AlphaLightGray30};
    }

    &:focus-visible {
        outline: 2px solid ${Variables.Colors.b300};
        outline-offset: -2px;
    }
`;

export const TabIcon = styled.span<{ active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
        fill: ${props => props.active ? Variables.Swatches.Gray8 : Variables.Swatches.Gray7};
    }
`;

export const TabLabel = styled.span<{ active: boolean }>`
    font-family: 'Calibre', ${Variables.Fonts.calibre};
    font-style: normal;
    font-weight: ${props => props.active ? '500' : '400'};
    font-size: 13px;
    line-height: 16px;
    color: ${props => props.active ? Variables.Swatches.Gray9 : Variables.Swatches.Gray8};
    white-space: nowrap;
    flex-shrink: 0;
`;

export const TabDivider = styled.div`
    width: 2px;
    height: 30%;
    background-color: ${Variables.ThemeLight.BorderB1};
    flex-shrink: 0;
`;

/**
 * The panel this container renders for its `children`.
 *
 * It was `display: none` from the component's first commit — a placeholder that nobody exercised, because every
 * caller so far rendered its own panel. The next one that passed children put a whole page in the DOM and none of it
 * on screen, and a text-based e2e passed against it (PP-24). It renders now.
 *
 * The wrapper is a flex column, so the panel is a flex item: it fills what is left under the row, and `min-height: 0`
 * lets a scrolling child scroll instead of growing past the viewport. It only has height to give when the wrapper
 * itself fills — see `TabContainerWrapper`.
 *
 * Inside, the panel is an ordinary block, so a card in it is CONTENT-height rather than stretched. The same card
 * placed directly in a page row would fill that row instead. Neither is wrong; pick the one you meant.
 */
export const TabContent = styled.div`
    flex: 1 1 auto;
    min-height: 0;
`;


/**
 * The count beside a tab label — `All 26`.
 *
 * `ITab.count` has been on the interface since this component was written and was never rendered,
 * so a caller could pass one and see nothing. It renders now.
 */
export const TabCount = styled.span<{ active: boolean }>`
    color: ${(props): string => (props.active ? Variables.Swatches.Gray8 : Variables.Swatches.Gray6)};
    font-size: 13px;
    font-weight: 400;
    margin-left: 6px;
`;
